import { NewspaperIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { styled } from "simplydo/react/styled";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import type { CompanyBlog as CompanyBlogType } from "components/innointel/types";

import { InfoBoxWidget, InfoBoxWidgetTitleArea } from "../Styles";

const Feed = styled.section`
  & + & {
    margin-top: 24px;
  }

  .feed-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin: 16px 0 12px;
    font-size: 15px;
    font-weight: 600;
    color: #111418;

    a {
      font-size: 12px;
      font-weight: 400;
      color: #5b6470;
    }
  }
`;

const Articles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const Article = styled.a`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  background: #fff;
  color: inherit;
  text-decoration: none;

  &:hover {
    border-color: #c8ced6;
    box-shadow: 0 2px 8px rgba(17, 20, 24, 0.08);
  }

  .image {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 16 / 9;
    background: #f1f3f5;
    color: #9aa3ad;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px 12px;
  }

  .date {
    font-size: 12px;
    color: #5b6470;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    color: #111418;
  }

  .excerpt {
    display: -webkit-box;
    overflow: hidden;
    font-size: 13px;
    line-height: 1.45;
    color: #3d4550;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
`;

const ImagePlaceholder = () => <Icon icon={NewspaperIcon} size="large" />;

type CompanyBlogProps = { blogs: CompanyBlogType[] };

const CompanyBlog = ({ blogs }: CompanyBlogProps) => {
  if (!blogs.length) return null;
  const articles = blogs.reduce(
    (total, blog) => total + (blog.entries?.length ?? 0),
    0,
  );
  return (
    <InfoBoxWidget>
      <InfoBoxWidgetTitleArea>
        <Icon icon={NewspaperIcon} />
        <div>
          <H3>Blogs</H3>
          <span>
            {articles} {articles === 1 ? "article" : "articles"}
          </span>
        </div>
      </InfoBoxWidgetTitleArea>
      {blogs.map((blog, blogIndex) => (
        <Feed key={blog.link ?? blog.title ?? blogIndex}>
          <div className="feed-title">
            {blog.title}
            {blog.link ? (
              <a href={blog.link} target="_blank" rel="noreferrer noopener">
                {blog.link
                  .replace(/^https?:\/\/(www\.)?/, "")
                  .replace(/\/$/, "")}
              </a>
            ) : null}
          </div>
          <Articles>
            {(blog.entries ?? []).map((article, articleIndex) => (
              <Article
                key={article.link ?? `${article.title}-${articleIndex}`}
                href={article.link}
                target="_blank"
                rel="noreferrer noopener"
              >
                <div className="image">
                  {article.imageUrl ? (
                    <ImageWithFallback
                      src={article.imageUrl}
                      fallbackElement={<ImagePlaceholder />}
                    />
                  ) : (
                    <ImagePlaceholder />
                  )}
                </div>
                <div className="text">
                  {article.publishedAt ? (
                    <span className="date">
                      {formatDate(article.publishedAt, "d MMM yyyy")}
                    </span>
                  ) : null}
                  <span className="title">{article.title || "Untitled"}</span>
                  {article.excerpt ? (
                    <span className="excerpt">{article.excerpt}</span>
                  ) : null}
                </div>
              </Article>
            ))}
          </Articles>
        </Feed>
      ))}
    </InfoBoxWidget>
  );
};

export default CompanyBlog;
